import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { API_BASE_URL } from '../../../config/api';

const COOGoalsReview = () => {
  const baseUrl = API_BASE_URL;
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const allGoals = (Array.isArray(response.data) ? response.data : [])
          .flatMap((kra) =>
            Array.isArray(kra.goals)
              ? kra.goals.map((g) => ({
                  ...g,
                  kra_name: kra.name || kra.title,
                }))
              : [],
          )
          .filter(
            (goal) => goal.status === 'complete' || goal.status === 'completed',
          );

        setGoals(allGoals);

        const years = Array.from(
          new Set(
            allGoals
              .map((g) => g?.year)
              .filter((y) => y !== undefined && y !== null),
          ),
        ).sort((a, b) => a - b);

        setAvailableYears(years);
        const effectiveYear =
          years.length && years.includes(selectedYear)
            ? selectedYear
            : years[years.length - 1] || selectedYear;
        setSelectedYear(effectiveYear);
        setFilteredGoals(
          allGoals.filter((g) =>
            g.year !== undefined && g.year !== null
              ? g.year === effectiveYear
              : true,
          ),
        );
      } catch (err) {
        setError('Failed to fetch KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [baseUrl]);

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(
        goals.filter((g) =>
          g.year !== undefined && g.year !== null
            ? g.year === selectedYear
            : true,
        ),
      );
    }
  }, [selectedYear, goals]);

  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach((g) => {
      const kraName =
        (g.key_result_area &&
          (g.key_result_area.name || g.key_result_area)) ||
        g.kra_name ||
        'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });
    const totalHod = filteredGoals.reduce(
      (s, g) => s + (Number(g.hod_score) || 0),
      0,
    );
    const totalMetric = filteredGoals.reduce(
      (s, g) => s + (Number(g.metric) || 0),
      0,
    );
    const percentage =
      totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
    return {
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalHod,
      totalMetric,
      percentage,
      goals: filteredGoals,
    };
  };

  const generatePDF = async () => {
    try {
      const report = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      const primary = [59, 130, 246];
      const dark = [55, 65, 81];
      const light = [156, 163, 175];

      const wrap = (text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let line = '';
        words.forEach((w) => {
          const t = line ? line + ' ' + w : w;
          if (doc.getTextWidth(t) > maxWidth && line) {
            lines.push(line);
            line = w;
          } else {
            line = t;
          }
        });
        if (line) lines.push(line);
        return lines;
      };

      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [70, 40, 30, 30, 10];
        const headerH = 10;
        const rowH = 8;
        let x = 15;
        doc.setFillColor(...primary);
        doc.rect(15, startY, maxWidth, headerH, 'F');
        headers.forEach((h, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(h, x + 2, startY + 6);
          x += colWidths[i];
        });
        let y = startY + headerH;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        rows.forEach((row, rIdx) => {
          if (rIdx % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, y, maxWidth, rowH, 'F');
          }
          let xx = 15;
          let maxLines = 1;
          row.forEach((cell, i) => {
            const wl = wrap(cell, colWidths[i] - 4);
            maxLines = Math.max(maxLines, wl.length);
            wl.forEach((ln, li) => {
              doc.text(ln, xx + 2, y + 5 + li * 3.5);
            });
            xx += colWidths[i];
          });
          y += Math.max(rowH, maxLines * 3.5 + 2);
        });
        return y;
      };

      let y = 20;
      doc.setTextColor(...primary);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('KPI Reviews Report', 105, y, { align: 'center' });
      y += 12;
      doc.setTextColor(...dark);
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text(`Year: ${report.year}`, 105, y, { align: 'center' });
      y += 16;
      doc.setFillColor(248, 250, 252);
      doc.rect(15, y, 180, 25, 'F');
      doc.setDrawColor(...primary);
      doc.rect(15, y, 180, 25);
      y += 8;
      doc.setTextColor(...dark);
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text('Summary', 20, y);
      y += 7;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(
        `Total HOD Score: ${report.totalHod} / ${report.totalMetric}`,
        20,
        y,
      );
      y += 5;
      doc.text(`Performance: ${report.percentage}%`, 20, y);
      y += 15;

      report.kras.forEach(([kraName, kraGoals]) => {
        if (y > 250) {
          doc.addPage();
          y = 20;
        }
        doc.setFillColor(...primary);
        doc.rect(15, y, 180, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName}`, 20, y + 8);
        y += 15;
        const headers = ['Activity/KPI', 'Standard', 'Metric', 'HOD', ''];
        const rows = kraGoals.map((g) => [
          g.activity || 'N/A',
          g.performance_standard || 'N/A',
          g.metric !== undefined ? g.metric : '-',
          g.hod_score !== undefined ? g.hod_score : '-',
          '',
        ]);
        const kraTotal = kraGoals.reduce(
          (s, g) => s + (Number(g.hod_score) || 0),
          0,
        );
        const kraMetric = kraGoals.reduce(
          (s, g) => s + (Number(g.metric) || 0),
          0,
        );
        rows.push([`KRA Total`, '', `${kraMetric}`, `${kraTotal}`, '']);
        y = drawTable(headers, rows, y);
        y += 10;
      });

      const pages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setTextColor(...light);
        doc.setFontSize(8);
        doc.text('Generated by PMS', 15, 290);
        doc.text(`Page ${i} of ${pages}`, 195, 290, { align: 'right' });
      }
      doc.save(`KPI Reviews Report ${selectedYear}.pdf`);
    } catch (e) {
      alert('Failed to generate PDF');
    }
  };

  return (
    <div
      data-sidebar-skin="dark"
      data-header-skin="light"
      data-navbar-brand-skin="dark"
      data-sidebar-state="expand"
    >
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">KPI Reviews</h4>
              <p className="text-muted mb-0 fs-13">
                View your completed KPIs and COO scores
              </p>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 fs-14 text-muted">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                >
                  {(availableYears.length ? availableYears : [selectedYear])
                    .sort((a, b) => a - b)
                    .map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                </select>
              </div>

              {filteredGoals.length > 0 && (
                <button
                  type="button"
                  onClick={generatePDF}
                  className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                  title="Download PDF Report"
                >
                  <i className="ti ti-file-text fs-16" />
                  <i className="ti ti-download fs-14" />
                  <span>Download PDF</span>
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="card shadow-sm p-5 text-center">
              <div className="spinner-border text-primary mx-auto mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0 fs-13">Loading KPI reviews...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger shadow-sm" role="alert">
              <i className="ti ti-alert-circle me-2" />
              {error}
            </div>
          ) : filteredGoals.length === 0 ? (
            <div className="card shadow-sm p-5 text-center text-muted">
              <i className="ti ti-target-off fs-36 mb-2 text-secondary opacity-50" />
              No completed KPIs found.
            </div>
          ) : (
            <>
              {(() => {
                const kraMap = {};
                filteredGoals.forEach((g) => {
                  const kraName =
                    (g.key_result_area &&
                      (g.key_result_area.name || g.key_result_area)) ||
                    g.kra_name ||
                    'Uncategorized';
                  if (!kraMap[kraName]) kraMap[kraName] = [];
                  kraMap[kraName].push(g);
                });
                const grouped = Object.entries(kraMap);

                return grouped.map(([kra, kraGoals], gidx) => (
                  <div key={kra || gidx} className="card shadow-sm mb-4">
                    <div className="card-header bg-light border-bottom py-3">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        KRA: <span className="text-primary">{kra}</span>
                      </h6>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Activity / Key Performance Indicator</th>
                            <th>Performance Standard / Target</th>
                            <th className="text-center">Metric</th>
                            <th className="text-center">Self Score</th>
                            <th className="text-center">COO Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kraGoals.map((goal, idx) => (
                            <tr key={goal.id || idx}>
                              <td>{goal.activity || '[No Activity]'}</td>
                              <td>
                                {goal.performance_standard !== undefined
                                  ? goal.performance_standard
                                  : '-'}
                              </td>
                              <td className="text-center fw-semibold">
                                {goal.metric !== undefined ? goal.metric : '-'}
                              </td>
                              <td className="text-center text-primary fw-bold">
                                {goal.score !== undefined ? goal.score : '-'}
                              </td>
                              <td className="text-center text-success fw-bold">
                                {goal.hod_score !== undefined
                                  ? goal.hod_score
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-light fw-bold">
                            <td
                              colSpan={4}
                              className="text-end text-uppercase fs-12 text-muted"
                            >
                              KRA Final
                            </td>
                            <td className="text-center text-dark">
                              {kraGoals.reduce(
                                (s, g) => s + (Number(g.hod_score) || 0),
                                0,
                              )}{' '}
                              /{' '}
                              {kraGoals.reduce(
                                (s, g) => s + (Number(g.metric) || 0),
                                0,
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}

              {filteredGoals.length > 0 &&
                (() => {
                  const totalHod = filteredGoals.reduce(
                    (sum, g) => sum + (Number(g.hod_score) || 0),
                    0,
                  );
                  const totalMetric = filteredGoals.reduce(
                    (sum, g) => sum + (Number(g.metric) || 0),
                    0,
                  );
                  const pct =
                    totalMetric > 0
                      ? ((totalHod / totalMetric) * 100).toFixed(2)
                      : '0.00';
                  return (
                    <div className="card shadow-sm mb-4 bg-primary-subtle border border-primary-subtle">
                      <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                          <h6 className="fw-bold text-dark mb-1">Final Total</h6>
                          <p className="text-muted fs-13 mb-0">
                            Overall performance metrics for {selectedYear}
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-4">
                          <div className="text-center">
                            <span className="text-muted fs-12 d-block">
                              Total Score
                            </span>
                            <strong className="text-dark fs-16">
                              {totalHod} / {totalMetric}
                            </strong>
                          </div>
                          <div className="text-center">
                            <span className="text-muted fs-12 d-block">
                              Performance
                            </span>
                            <span
                              className={`badge fs-14 px-3 py-2 rounded-pill ${pct >= 80 ? 'bg-success' : pct >= 60 ? 'bg-warning text-dark' : 'bg-danger'}`}
                            >
                              {pct}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>

        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All
            Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default COOGoalsReview;
